/**
 * usePrivateKeyExport.js — Export de clé privée (wallet GENERATED uniquement).
 *
 * Gère les 3 étapes de l'export de clé (états KEY_OTP_REQUEST / KEY_OTP_VERIFY / KEY_DISPLAY) :
 *
 *   Step 1 — requestOtp :
 *     POST /auth/wallet/key { walletId, requestOtp: true }
 *     → succès : dispatch OTP_SENT(maskedEmail)
 *     → erreur : dispatch OTP_SEND_ERROR(err)
 *
 *   Step 2 — verifyOtp :
 *     POST /auth/wallet/key { walletId, password, otpCode }
 *     → succès : dispatch OTP_VERIFIED(keyMaterial) — keyMaterial stocké UNIQUEMENT en React memory
 *     → erreur : dispatch OTP_VERIFY_ERROR(err) + incrément compteur (lockout à 5 tentatives)
 *
 *   Step 3 — KEY_DISPLAY :
 *     - Timer 120 secondes → dispatch KEY_TIMER_EXPIRED → IDLE (keyMaterial = null)
 *     - Bouton "J'ai sauvegardé mes clés" → dispatch KEYS_SAVED → IDLE (keyMaterial = null)
 *     - Téléchargement keystore JSON (Blob, aucun round-trip serveur)
 *     - Copie de la phrase mnémotechnique (navigator.clipboard)
 *
 * Règle de sécurité §8.4 / §9 SPEC :
 *   keyMaterial (keystore + mnemonic) n'est JAMAIS écrit dans localStorage,
 *   sessionStorage, ou Redux. Il vit uniquement dans useReducer (useWalletStateMachine).
 *   Ce hook ne stocke PAS keyMaterial dans son propre state local.
 *
 * Aucun import Redux — §8.7.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { ACTION_TYPES }                              from './useWalletStateMachine';
import { requestKeyExportOtp, exportKey }            from '../services/WalletAuthService';

/* Durée d'affichage de la clé avant effacement automatique (§8.5 Step 3 SPEC) */
const KEY_DISPLAY_DURATION_MS = 120_000; // 120 secondes

/**
 * Masque une adresse email pour affichage sécurisé.
 * Ex : "jonathan@example.com" → "j*******@e*****.com"
 *
 * @param {string} email
 * @returns {string}
 */
function maskEmail(email) {
  if (!email || !email.includes('@')) return email ?? '';
  const [local, domain] = email.split('@');
  const maskedLocal  = local[0] + '*'.repeat(Math.max(1, local.length - 1));
  const domainParts  = domain.split('.');
  const maskedDomain = domainParts[0][0] + '*'.repeat(Math.max(1, domainParts[0].length - 1));
  const tld          = domainParts.slice(1).join('.');
  return `${maskedLocal}@${maskedDomain}.${tld}`;
}

/**
 * usePrivateKeyExport
 *
 * @param {object}   params
 * @param {function} params.dispatch      - dispatch de useWalletStateMachine
 * @param {string}   params.apiBaseUrl    - URL de base de l'API
 * @param {object|null} params.keyMaterial - keyMaterial issu du state machine (KEY_DISPLAY seulement)
 * @param {string}   params.currentState  - état courant de la machine (pour déclencher le timer)
 *
 * @returns {{
 *   /* Step 1
 *   walletId:         number|null,
 *   setWalletId:      function,
 *   otpLoading:       boolean,
 *   otpError:         string|null,
 *   handleRequestOtp: function(walletId: number) => Promise<void>,
 *
 *   /* Step 2
 *   otpCode:          string,
 *   setOtpCode:       function,
 *   exportPassword:   string,
 *   setExportPassword:function,
 *   verifyLoading:    boolean,
 *   verifyError:      string|null,
 *   handleVerifyOtp:  function() => Promise<void>,
 *
 *   /* Step 3
 *   countdown:        number,    — secondes restantes (0–120)
 *   isMnemonicBlurred:boolean,
 *   revealMnemonic:   function() => void,
 *   blurMnemonic:     function() => void,
 *   handleCopyMnemonic:function() => Promise<void>,
 *   handleDownloadKeystore: function() => void,
 *   handleKeysSaved:  function() => void,
 *   copySuccess:      boolean,
 * }}
 */
export function usePrivateKeyExport({ dispatch, apiBaseUrl, keyMaterial, currentState }) {

  /* ── Step 1 — walletId + OTP request ──────────────────────────────────────── */
  const [walletId,   setWalletId]   = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError,   setOtpError]   = useState(null);

  /* ── Step 2 — OTP verify ──────────────────────────────────────────────────── */
  const [otpCode,         setOtpCode]         = useState('');
  const [exportPassword,  setExportPassword]  = useState('');
  const [verifyLoading,   setVerifyLoading]   = useState(false);
  const [verifyError,     setVerifyError]     = useState(null);

  /* ── Step 3 — KEY_DISPLAY ─────────────────────────────────────────────────── */
  const [countdown,        setCountdown]       = useState(KEY_DISPLAY_DURATION_MS / 1000);
  const [isMnemonicBlurred, setIsMnemonicBlurred] = useState(true);
  const [copySuccess,      setCopySuccess]     = useState(false);

  /* Refs pour les timers */
  const countdownRef        = useRef(null);
  const blurInactivityRef   = useRef(null);

  /* ── Timer 120s actif uniquement en état KEY_DISPLAY ─────────────────────── */
  useEffect(() => {
    if (currentState !== 'KEY_DISPLAY') {
      /* Nettoyer les timers si on quitte KEY_DISPLAY */
      clearInterval(countdownRef.current);
      clearTimeout(blurInactivityRef.current);
      setCountdown(KEY_DISPLAY_DURATION_MS / 1000);
      setIsMnemonicBlurred(true);
      return;
    }

    /* Démarrer le compte à rebours */
    setCountdown(KEY_DISPLAY_DURATION_MS / 1000);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          dispatch({ type: ACTION_TYPES.KEY_TIMER_EXPIRED });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownRef.current);
      clearTimeout(blurInactivityRef.current);
    };
  }, [currentState, dispatch]);

  /* ── Re-flou automatique après 30s d'inactivité (§8.5 Step 3) ──────────── */
  const resetBlurTimer = useCallback(() => {
    clearTimeout(blurInactivityRef.current);
    blurInactivityRef.current = setTimeout(() => {
      setIsMnemonicBlurred(true);
    }, 30_000);
  }, []);

  const revealMnemonic = useCallback(() => {
    setIsMnemonicBlurred(false);
    resetBlurTimer();
  }, [resetBlurTimer]);

  const blurMnemonic = useCallback(() => {
    setIsMnemonicBlurred(true);
    clearTimeout(blurInactivityRef.current);
  }, []);

  /* ── handleRequestOtp ────────────────────────────────────────────────────── */
  const handleRequestOtp = useCallback(async (selectedWalletId) => {
    setOtpError(null);
    const wid = selectedWalletId ?? walletId;
    if (!wid) {
      const msg = 'Aucun wallet sélectionné pour l\'export.';
      setOtpError(msg);
      dispatch({ type: ACTION_TYPES.OTP_SEND_ERROR, payload: { message: msg } });
      return;
    }
    setWalletId(wid);
    setOtpLoading(true);
    try {
      await requestKeyExportOtp(apiBaseUrl, wid);
      /* Le backend ne retourne pas l'email masqué dans sa réponse.
         On dispatch OTP_SENT sans maskedEmail ; le panel peut afficher un message générique.
         Si le backend évolue pour retourner un maskedEmail, l'ajouter ici. */
      dispatch({
        type: ACTION_TYPES.OTP_SENT,
        payload: { maskedEmail: null },
      });
    } catch (err) {
      const msg = err?.message ?? 'Could not send the OTP code. Please try again.';
      setOtpError(msg);
      dispatch({ type: ACTION_TYPES.OTP_SEND_ERROR, payload: { message: msg } });
    } finally {
      setOtpLoading(false);
    }
  }, [dispatch, apiBaseUrl, walletId]);

  /* ── handleVerifyOtp ─────────────────────────────────────────────────────── */
  const handleVerifyOtp = useCallback(async () => {
    setVerifyError(null);

    if (!otpCode || otpCode.length !== 6) {
      const msg = 'Please enter a valid 6-digit OTP code.';
      setVerifyError(msg);
      dispatch({ type: ACTION_TYPES.OTP_VERIFY_ERROR, payload: { message: msg } });
      return;
    }
    if (!exportPassword) {
      const msg = 'Password is required to decrypt the key.';
      setVerifyError(msg);
      dispatch({ type: ACTION_TYPES.OTP_VERIFY_ERROR, payload: { message: msg } });
      return;
    }

    setVerifyLoading(true);
    try {
      const result = await exportKey(apiBaseUrl, {
        walletId,
        password: exportPassword,
        otpCode,
      });
      /* result = { keystore: string, mnemonic: string, address: string }
         keyMaterial est stocké UNIQUEMENT dans le reducer useWalletStateMachine
         (React memory). Ce hook ne le stocke jamais localement. */
      dispatch({
        type: ACTION_TYPES.OTP_VERIFIED,
        payload: {
          keystore:  result?.keystore  ?? '',
          mnemonic:  result?.mnemonic  ?? '',
          address:   result?.address   ?? '',
        },
      });
      /* Réinitialiser les champs sensibles dès que les clés sont remontées */
      setOtpCode('');
      setExportPassword('');
    } catch (err) {
      const msg = err?.message ?? 'Invalid OTP code or password.';
      setVerifyError(msg);
      dispatch({ type: ACTION_TYPES.OTP_VERIFY_ERROR, payload: { message: msg } });
    } finally {
      setVerifyLoading(false);
    }
  }, [dispatch, apiBaseUrl, walletId, otpCode, exportPassword]);

  /* ── handleCopyMnemonic (§8.5 Step 3) ───────────────────────────────────── */
  const handleCopyMnemonic = useCallback(async () => {
    if (!keyMaterial?.mnemonic) return;
    try {
      await navigator.clipboard.writeText(keyMaterial.mnemonic);
      setCopySuccess(true);
      /* Remettre le succès à false après 2s */
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      /* Clipboard non disponible — ignorer silencieusement */
    }
  }, [keyMaterial]);

  /* ── handleDownloadKeystore (§8.5 Step 3) ───────────────────────────────── */
  const handleDownloadKeystore = useCallback(() => {
    if (!keyMaterial?.keystore) return;
    const addrSuffix = keyMaterial.address
      ? keyMaterial.address.slice(0, 6) + '...' + keyMaterial.address.slice(-4)
      : 'wallet';
    const filename = `keystore-${addrSuffix}.json`;
    const blob = new Blob([keyMaterial.keystore], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [keyMaterial]);

  /* ── handleKeysSaved (§8.5 Step 3) ──────────────────────────────────────── */
  const handleKeysSaved = useCallback(() => {
    dispatch({ type: ACTION_TYPES.KEYS_SAVED });
  }, [dispatch]);

  return {
    /* Step 1 */
    walletId,
    setWalletId,
    otpLoading,
    otpError,
    handleRequestOtp,

    /* Step 2 */
    otpCode,
    setOtpCode,
    exportPassword,
    setExportPassword,
    verifyLoading,
    verifyError,
    handleVerifyOtp,

    /* Step 3 */
    countdown,
    isMnemonicBlurred,
    revealMnemonic,
    blurMnemonic,
    handleCopyMnemonic,
    handleDownloadKeystore,
    handleKeysSaved,
    copySuccess,
  };
}

export default usePrivateKeyExport;
